/**
 * @jsxRuntime classic
 * @jsx jsx
 */

import { type ReactNode, useEffect } from 'react';

import { useIntl } from 'react-intl';

import { cssMap, jsx } from '@atlaskit/css';
import { Box, Pressable, Text } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import { useAnalytics } from '@atlassian/conversation-assistant-instrumentation/controllers/analytics';

import { useAppOAuth } from '../chat-mode-menu/app-filter/oauth/useAppOAuth';

import { messages } from './messages';

const styles = cssMap({
	tipWrapper: {
		paddingInline: token('space.050'),
		paddingBlockEnd: token('space.150'),
		color: token('color.text.subtlest'),
		font: token('font.body.small'),
	},
	connectLink: {
		backgroundColor: 'transparent',
		paddingTop: token('space.0'),
		paddingRight: token('space.0'),
		paddingBottom: token('space.0'),
		paddingLeft: token('space.0'),
		color: token('color.link'),
		font: token('font.body.small'),
		'&:hover': {
			color: token('color.link.pressed'),
			textDecoration: 'underline',
		},
	},
});

export interface ConnectorSourceConnectionTipProps {
	friendlyName: string;
	appSource: string;
	outboundAuthUrl: string;
	onConnected?: () => void;
	/** Controls which tip variant to show. Defaults to 'reactive' (existing behaviour). */
	tipType?: 'reactive' | 'proactive';
}

export const ConnectorSourceConnectionTip = ({
	friendlyName,
	appSource,
	outboundAuthUrl,
	onConnected,
	tipType = 'reactive',
}: ConnectorSourceConnectionTipProps): JSX.Element | null => {
	const { formatMessage } = useIntl();
	const { sendAnalyticsEvent } = useAnalytics();

	useEffect(() => {
		sendAnalyticsEvent({
			eventType: 'track',
			action: 'viewed',
			actionSubject: 'rovoChatConnectorSourceTip',
			attributes: {
				appSource,
				tipType,
			},
		});
	}, [appSource, sendAnalyticsEvent, tipType]);

	const { onAuthorize } = useAppOAuth({
		friendlyName,
		appSource,
		outboundAuthUrl,
		onCompletedAction: onConnected,
		analyticsAttributes: {
			source: tipType === 'proactive' ? 'smartLinkContextBanner' : 'connectionTip',
		},
	});

	const linkRenderer = (chunks: ReactNode[]) => (
		<Pressable xcss={styles.connectLink} onClick={onAuthorize}>
			{chunks}
		</Pressable>
	);

	if (!outboundAuthUrl) {
		return null;
	}

	return (
		<Box xcss={styles.tipWrapper}>
			<Text size="small" weight="medium" color="color.text">
				{formatMessage(messages.tipLabel)}{' '}
			</Text>
			{tipType === 'proactive'
				? formatMessage(messages.proactiveTip, {
						appName: friendlyName,
						link: linkRenderer,
					})
				: formatMessage(messages.connectAppTip, {
						appName: friendlyName,
						link: linkRenderer,
					})}
		</Box>
	);
};
